import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Alert } from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useResources } from "../../hooks/useRedux";
import { setResourcesUploadProgress, clearResourcesUploadProgress, updateResourceAction } from "../../store";
import ResourceForm from "./ResourceForm";
import { Resource } from "../../store/slices/types";
import { endPoints } from "../../services/endpoints";
import { API_BASE_URL } from "../../config/api";
import axios from "axios";

const EditResource: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { resources, loading, uploadProgress } = useResources();
  const [saving, setSaving] = useState(false);

  const resource = resources.find((r) => (r._id || r.id) === id) as
    | Resource
    | undefined;

  const handleSave = async (resourceData: Partial<Resource>, mediaFile?: File) => {
    if (!id) {
      toast.error("Invalid resource ID");
      return;
    }

    try {
      setSaving(true);
      dispatch(clearResourcesUploadProgress());

      // Create FormData with resource data and media file
      const formData = new FormData();
      
      // Append all resource fields
      if (resourceData.title) formData.append('title', resourceData.title);
      if (resourceData.description) formData.append('description', resourceData.description);
      if (resourceData.shortDescription) formData.append('shortDescription', resourceData.shortDescription);
      if (resourceData.type) formData.append('type', resourceData.type);
      if (resourceData.category) formData.append('category', resourceData.category);
      if (resourceData.content) formData.append('content', resourceData.content);
      if (resourceData.duration !== undefined) formData.append('duration', String(resourceData.duration));
      if (resourceData.author) formData.append('author', resourceData.author);
      if (resourceData.source) formData.append('source', resourceData.source);
      if (resourceData.isActive !== undefined) formData.append('isActive', String(resourceData.isActive));
      if (resourceData.color) formData.append('color', resourceData.color);
      formData.append('updatedAt', String(Date.now()));
      
      // Append tags as JSON string
      if (resourceData.tags && Array.isArray(resourceData.tags)) {
        formData.append('tags', JSON.stringify(resourceData.tags));
      }
      
      // Append media file if provided, otherwise keep existing URL
      if (mediaFile) {
        const fieldName = resourceData.type === "video" ? "video" : resourceData.type === "audio" ? "audio" : "pdf";
        formData.append(fieldName, mediaFile);
      } else if (resourceData.url) {
        formData.append('url', resourceData.url);
      }
      
      const token = localStorage.getItem('authToken');
      
      // Single API call with progress tracking
      const response = await axios.put(
        `${API_BASE_URL}${endPoints.resources.update(id)}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: token ? `Bearer ${token}` : '',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              dispatch(setResourcesUploadProgress(percentCompleted));
            }
          },
        }
      );
      
      dispatch(clearResourcesUploadProgress());
      
      if (response.data?.success || response.data) {
        const updatedResource = { ...(response.data?.data || response.data), _id: id };
        dispatch(updateResourceAction({ id, data: updatedResource }));
        toast.success("resource is updated");
        navigate("/resources");
      } else {
        toast.error("Failed to update resource");
      }
    } catch (error: any) {
      console.error("Failed to update resource:", error);
      dispatch(clearResourcesUploadProgress());
      toast.error(
        `❌ Failed to update resource: ${
          error?.response?.data?.message || error?.message || "Unknown error"
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/resources");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!resource) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          Resource not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          mb: 4,
        }}
      >
        <Typography variant='h4' fontWeight='700' gutterBottom>
          Edit Resource
        </Typography>
        <Typography variant='body1' sx={{ opacity: 0.9 }}>
          Update your wellness resource
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <ResourceForm
          resource={resource}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          uploadProgress={uploadProgress}
        />
      </Paper>
    </Box>
  );
};

export default EditResource;
